import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEmployee } from '../../../context/employee';
import { subscribe, unsubscribe } from '../../../context/events';
import { useSite } from '../../../context/site';
import { MSG, PLeft } from '../../../shared';
import ServiceDateTemplate from '../../service-date-template';
import ServiceProgramCreate from './create';
import ServiceProgramUpdate from './update';

function TableServicesProgram({
    initLimit = 5,
    initSkip = 0,
    initFilters = [],
    initSortData = { dateService: -1 }
}) {
    const toast = useRef(null);
    const initData = useRef({
        limit: initLimit,
        skip: initSkip,
        filters: initFilters,
        sortData: initSortData
    });

    const { employee } = useEmployee();
    const { GetServicesProgram } = useSite();

    const [initialized, setInitialized] = useState(false);
    const [limit, setLimit] = useState(initLimit);
    const [skip, setSkip] = useState(initSkip);
    const [filters, setFilters] = useState(initFilters);
    const [sortData, setSortData] = useState(initSortData);
    const [sortField, setSortField] = useState('dateService');
    const [sortOrder, setSortOrder] = useState(-1);
    const [loading, setLoading] = useState(false);
    const [loadingError, setLoadingError] = useState('');
    const [rows, setRows] = useState([]);
    const [recordCount, setRecordCount] = useState(0);
    const [filteredRecordCount, setFilteredRecordCount] = useState(0);
    const [openCreate, setOpenCreate] = useState(false);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [updateData, setUpdateData] = useState({});
    const [refresh, setRefresh] = useState(false);

    const init = useCallback(() => {
        GetServicesProgram(
            {
                filters: initData.current.filters,
                skip: initData.current.skip,
                limit: initData.current.limit,
                sorts: initData.current.sortData
            },
            (res) => {
                if (res.status === 200) {
                    // console.log('init: success');
                    setRows(res.data.servicesProgram);
                    setRecordCount(res.data.count);
                    setFilteredRecordCount(res.data.filterCount);
                } else {
                    // console.log('init: failure');
                    setLoadingError(res.message);
                }

                // set values from init data ref
                setLimit(initData.current.limit);
                setSkip(initData.current.skip);
                setFilters(initData.current.filters);
                setSortData(initData.current.sortData);

                // set the table sort field from the object
                if (Object.keys(initData.current.sortData).length > 0) {
                    const [sorts] = Object.entries(initData.current.sortData);
                    const _sortField = sorts[0];
                    const _sortOrder = sorts[1];

                    setSortField(_sortField);
                    setSortOrder(_sortOrder);
                }

                setInitialized(true);
                setLoading(false);
            }
        );
    }, [GetServicesProgram]);

    const pagination = useCallback(() => {
        GetServicesProgram({ filters, skip, limit, sorts: sortData }, (res) => {
            if (res.status === 200) {
                // console.log('pagination: success');
                setRows(res.data.servicesProgram);
                setRecordCount(res.data.count);
                setFilteredRecordCount(res.data.filterCount);
            } else {
                // console.log('pagination: failure');
                setLoadingError(res.message);
            }

            setLoading(false);
        });
    }, [filters, skip, limit, sortData, GetServicesProgram]);

    const onRefresh = useCallback(() => {
        GetServicesProgram({ filters, skip, limit, sorts: sortData }, (res) => {
            if (res.status === 200) {
                console.log('refresh: success');
                setRows(res.data.servicesProgram);
                setRecordCount(res.data.count);
                setFilteredRecordCount(res.data.filterCount);
            } else {
                console.log('refresh: failure');
                setLoadingError(res.message);
            }

            setLoading(false);
            setRefresh(false);
        });
    }, [filters, skip, limit, sortData, GetServicesProgram]);

    const processEvent = useCallback(
        (event) => {
            const { detail: res, type: action } = event;

            do {
                if (res.status !== 200) {
                    console.warn(res.message);
                    break;
                }

                if (![MSG.create.service.program, MSG.update.service.program].includes(action)) {
                    break;
                }

                const filterFound = initData.current.filters.find(
                    (f) => f.field === 'employee' && f.value === employee._id
                );
                const serviceHasEmployee = res.data.service.employee._id === employee._id;

                if (action === MSG.create.service.program) {
                    if (filterFound && serviceHasEmployee) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Program Service Created',
                            detail: `${res.data.service.serviceType.itemName}`,
                            life: 3000
                        });

                        setLoading(true);
                        setRefresh(true);
                    }

                    break;
                }

                if (filterFound) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Program Service Updated',
                        detail: `${res.data.service.serviceType.itemName}`,
                        life: 3000
                    });

                    setLoading(true);
                    setRefresh(true);
                }
            } while (0);
        },
        [employee._id]
    );

    const ActionsDisplay = (service) => {
        return (
            <>
                <Button
                    icon="pi pi-user-edit"
                    size="small"
                    rounded
                    severity="help"
                    onClick={() => {
                        setUpdateData(service);
                        setOpenUpdate(true);
                    }}
                />
            </>
        );
    };

    const NotesDisplay = (service) => {
        if (!service.notes) {
            return '';
        }

        return <span dangerouslySetInnerHTML={{ __html: service.notes }}></span>;
    };

    // App Events
    useEffect(() => {
        subscribe(MSG.create.service.program, processEvent);
        subscribe(MSG.update.service.program, processEvent);

        return () => {
            unsubscribe(MSG.create.service.program, processEvent);
            unsubscribe(MSG.update.service.program, processEvent);
        };
    }, [processEvent]);

    // Init
    useEffect(() => {
        if (initialized) {
            // console.log('init: already initialized');
            return;
        }

        // console.log('init: initializing');

        init();
    }, [initialized, init]);

    // Pagination
    useEffect(() => {
        if (!initialized) {
            // console.log('pagination: not initialized');
            return;
        }

        // console.log('pagination: getting rows');

        pagination();
    }, [initialized, pagination]);

    // Refresh
    useEffect(() => {
        if (!refresh) {
            return;
        }

        onRefresh();
    }, [refresh, onRefresh]);

    if (!initialized) {
        return (
            <Card title="Program Services">
                <ProgressSpinner />

                <p>Initializing</p>
            </Card>
        );
    }

    if (loadingError) {
        return (
            <Card title="Program Services">
                <h4>Error Loading Program Services</h4>

                <p>{loadingError}</p>
            </Card>
        );
    }

    return (
        <>
            <div className="grid">
                <div className="col">
                    <Button
                        icon="pi pi-user-plus"
                        rounded
                        severity="success"
                        onClick={() => {
                            setOpenCreate(true);
                        }}
                    />

                    <Button
                        className="ml-1"
                        icon="pi pi-refresh"
                        rounded
                        severity="help"
                        onClick={() => {
                            setLoading(true);
                            setRefresh(true);
                        }}
                    />
                </div>

                {/* TODO search */}
            </div>

            <DataTable
                value={rows}
                emptyMessage="No program services found"
                showGridlines
                removableSort
                sortField={sortField}
                sortOrder={sortOrder}
                loading={loading}
                onSort={(event) => {
                    const sortData = {};

                    sortData[event.sortField] = event.sortOrder;

                    setSortField(event.sortField);
                    setSortOrder(event.sortOrder);

                    if (event.sortField) {
                        setSortData({ ...sortData });
                    } else {
                        setSortData({});
                    }

                    setLoading(true);
                }}
            >
                <Column
                    field="dateService"
                    body={ServiceDateTemplate}
                    header="Service Date"
                    sortable
                ></Column>
                <Column field="employee.name" header="Employee" sortable></Column>
                <Column field="program.programName" header="Program" sortable></Column>
                <Column field="serviceType.itemName" header="Service Type" sortable></Column>
                <Column body={NotesDisplay} header="Notes"></Column>
                <Column
                    header="Actions"
                    bodyStyle={{ width: '150px' }}
                    body={ActionsDisplay}
                ></Column>
            </DataTable>

            <Paginator
                alwaysShow={true}
                leftContent={
                    <PLeft
                        filteredRecordCount={filteredRecordCount}
                        recordCount={recordCount}
                        skip={skip}
                        limit={limit}
                    />
                }
                first={skip}
                rows={limit}
                totalRecords={filters.length > 0 ? filteredRecordCount : recordCount}
                rowsPerPageOptions={[5, 10, 20, 30]}
                onPageChange={(pageEvent) => {
                    setSkip(pageEvent.first);
                    setLimit(pageEvent.rows);
                }}
            />

            {!openCreate ? null : (
                <ServiceProgramCreate
                    open={openCreate}
                    onClose={() => {
                        setOpenCreate(false);
                    }}
                />
            )}

            {!openUpdate ? null : (
                <ServiceProgramUpdate
                    open={openUpdate}
                    data={updateData}
                    onClose={() => {
                        setUpdateData({});
                        setOpenUpdate(false);
                    }}
                />
            )}

            <Toast ref={toast} />
        </>
    );
}

export default TableServicesProgram;
